import React, { useEffect, useState } from "react";
import { HiOutlineTicket } from "react-icons/hi";
import { Assort } from "../../../models/Assort";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "../../../../style/hovers.scss";
import { BiInfoCircle } from "react-icons/bi";
import HtmlTooltip from "@mui/material/Tooltip";
import { TicketAccess } from "../../../models/TicketAccess";
import "../../../../style/buttons.scss";
import getId from "../../../../utils/getId";
import { BasketItem } from "../../../models/BasketItem";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import ModalComponent from "../../../../features/ModalComponent";
import { useTranslation } from "react-i18next";
import { QntStateItem } from "../../../models/QntStateItem";
import EAQuantitySelector from "../../shared/QuantitySelector/EAQuantitySelector";

interface IProps {
  ticketLimit?: number;
  item: Assort;
  exercise?: TicketAccess;
  preBasket: BasketItem[];
  addItemToBasket: (assort: Assort, exercise?: TicketAccess) => void;
  removeItemFromBasket: (assort: Assort, exercise?: TicketAccess) => void;
  setItemQuantity?: (assort: Assort, newQuantity: number, exercise?: TicketAccess) => void;
  qntState?: QntStateItem[];
  allowManualInput?: boolean;
  eventCapacityLimit?: number;
} // optional for styling

const TicketLineSummary: React.FC<IProps> = ({
  item,
  exercise,
  preBasket,
  ticketLimit,
  addItemToBasket,
  removeItemFromBasket,
  setItemQuantity,
  qntState,
  allowManualInput = false,
  eventCapacityLimit,
}: IProps) => {
  const { t } = useTranslation();

  const getQnt = (value: Assort): number => {
    if (qntState && qntState.length > 0 && exercise) {
      const foundQnt = qntState.find(
        (item) => item.assortIds === getId(value) && item.exerciseIds === exercise.exerciseIds
      );
      return foundQnt ? foundQnt.count : 0;
    } else {
      const itemBasket = preBasket.find((item) => item.assortIds === getId(value));
      return itemBasket ? itemBasket.qnt : 0;
    }
  };

  useEffect(() => {
    if (item.maxSale === 0 || !item.maxSale) {
      setMaxQnt(99);
    } else {
      setMaxQnt(item.maxSale);
    }
    //eslint-disable-next-line
  }, []);
  const isDesktop = useDesktopSize();
  const [open, setOpen] = useState(false);
  const [maxQnt, setMaxQnt] = useState(0);
  const isDiscount = item.price !== item.listPrice;

  const ticketsInCurrentBasket = () => {
    if (qntState && qntState.length > 0 && exercise) {
      return qntState
        .filter((item) => item.exerciseIds === exercise.exerciseIds)
        .reduce((acc: number, item: QntStateItem) => acc + item.count, 0);
    } else {
      return preBasket.reduce((acc: number, item: BasketItem) => acc + item.qnt, 0);
    }
  };

  const handleQuantityChange = (newValue: number) => {
    if (!allowManualInput) return;

    if (setItemQuantity) {
      // Użyj bezpośredniego ustawienia ilości jeśli dostępne
      setItemQuantity(item, newValue, exercise);
    } else {
      // Fallback do starej logiki z pętlą (dla kompatybilności wstecznej)
      const currentQnt = getQnt(item);
      const diff = newValue - currentQnt;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          addItemToBasket(item, exercise);
        }
      } else if (diff < 0) {
        for (let i = 0; i < Math.abs(diff); i++) {
          removeItemFromBasket(item, exercise);
        }
      }
    }
  };

  const getEffectiveMax = () => {
    let effectiveMax = maxQnt;

    // Uwzględnij ticketLimit jeśli jest ustawiony
    if (ticketLimit !== undefined) {
      const currentQnt = getQnt(item);
      const remainingTickets = ticketLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingTickets);
    }

    // Uwzględnij eventCapacityLimit jeśli jest ustawiony
    if (eventCapacityLimit !== undefined) {
      const currentQnt = getQnt(item);
      const remainingEventCapacity = eventCapacityLimit - ticketsInCurrentBasket() + currentQnt;
      effectiveMax = Math.min(effectiveMax, remainingEventCapacity);
    }

    return Math.max(0, effectiveMax); // Nie może być ujemne
  };

  return (
    <>
      <div
        className={
          getQnt(item) > 0 ? "ticket-line-active ticket-line flex-between-center" : "ticket-line flex-between-center"
        }
      >
        <div className="ticket-line-left flex-start-center">
          <div className="flex-between-center">
            <HiOutlineTicket className="ticket-line-icon" />
            <b>{item.displayWebName || item.name}</b>
          </div>

          <>
            {item.description.length > 2 && (
              <>
                {isDesktop ? (
                  <HtmlTooltip
                    placement="right"
                    arrow={true}
                    title={
                      <>
                        <article
                          className="tooltip-description"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        ></article>
                      </>
                    }
                  >
                    <b>
                      <BiInfoCircle className="tooltip-icon" />
                    </b>
                  </HtmlTooltip>
                ) : (
                  <b onClick={() => setOpen(!open)}>
                    <BiInfoCircle className="tooltip-icon" />
                  </b>
                )}
              </>
            )}
          </>
        </div>

        <div className="ticket-line-right flex-end-center">
          <div className="price-container">
            <b className={`price ${isDiscount ? "price-through" : ""}`}>
              {item.listPrice.toFixed(2)} {t("zl")}
            </b>
            {isDiscount && (
              <b className={`price ${isDiscount ? "price-through-color" : ""}`}>
                {item.price.toFixed(2)} {t("zl")}
              </b>
            )}
          </div>

          <div className="qnt flex-end-center">
            <EAQuantitySelector
              value={getQnt(item)}
              onChange={handleQuantityChange}
              onIncrement={() => addItemToBasket(item, exercise)}
              onDecrement={() => removeItemFromBasket(item, exercise)}
              disabled={!allowManualInput}
              incrementDisabled={getQnt(item) >= getEffectiveMax()}
              decrementDisabled={getQnt(item) <= 0}
              min={0}
              max={getEffectiveMax()}
              className="quantity-selector--end"
            />
          </div>
        </div>
      </div>

      {open && (
        <div className="schedule-modal-info">
          <ModalComponent
            footer={false}
            open={open}
            closeModal={() => setOpen(false)}
            title={item.name}
            onAccept={() => setOpen(false)}
            acceptText={t("przeczytalem")}
          >
            {item.description.length > 1 ? (
              <p className="news-modal-body" dangerouslySetInnerHTML={{ __html: item.description }}></p>
            ) : (
              <p className="news-modal-body">{t("brak_opisu")}</p>
            )}
          </ModalComponent>
        </div>
      )}
    </>
  );
};

export default TicketLineSummary;
